import AOS from "aos";
import "aos/dist/aos.css";
import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faEnvelope, faLock } from "@fortawesome/free-solid-svg-icons";
import { Link, useNavigate } from "react-router-dom";
import AuthApiService from "../../ApiServices/AuthApiService";
import { useAuth } from "./AuthContext";
import { toast } from "react-toastify";
import { motion } from 'framer-motion';

export default function Login() {
  useEffect(() => {
    AOS.init({ duration: 1000 });
  }, []);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { setAuthInfo } = useAuth();

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage("");

    try {
      const credentials = { username: email, password, rememberMe: true };
      const result = await AuthApiService.signin(credentials);

      if (result.isSuccess) {
        const { user, roles, token } = result.data;
        localStorage.setItem("token", token);
        setAuthInfo(user, roles);
        navigate("/home");
      } else {
        toast.error("Invalid email or password.");
        setErrorMessage("Login failed. Please check your credentials.");
      }
    } catch (error) {
      setErrorMessage("Failed to sign in. Please try again.");
      console.error("Error during sign-in:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ rotateY: 90, opacity: 0 }}
      animate={{ rotateY: 0, opacity: 1 }}
      transition={{ duration: 1 }}
      className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center px-4"
    >
      <motion.div
        className="backdrop-blur-xl bg-white/10 border border-white/20 shadow-2xl rounded-3xl p-10 w-full max-w-md"
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 120 }}
        data-aos="fade-up"
      >
        <div className="text-center mb-6">
          <img src="/Images/logo.png" className="h-16 mx-auto" alt="Logo" />
          <h1 className="text-4xl font-bold text-white mt-4">Sign In</h1>
          <p className="text-sm text-white/80">Welcome back! Please log in to continue.</p>
        </div>

        <form className="space-y-5" onSubmit={handleSignIn}>
          <InputWithIcon
            id="email"
            icon={faEnvelope}
            type="email"
            placeholder="Your Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <div>
            <label className="block text-white mb-1">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                required
                placeholder="Your Password"
                className="w-full bg-white/20 text-white placeholder-white/70 px-11 py-3 rounded-lg border border-white/30 focus:ring-2 focus:ring-yellow-300 focus:outline-none"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <FontAwesomeIcon
                icon={showPassword ? faEyeSlash : faEye}
                onClick={() => setShowPassword(!showPassword)}
                className="absolute top-1/2 right-4 transform -translate-y-1/2 text-white/80 cursor-pointer"
              />
            </div>
          </div>

          <motion.button
            type="submit"
            disabled={loading}
            className={`w-full py-3 mt-4 bg-yellow-400 text-black rounded-xl font-bold tracking-wide hover:bg-yellow-300 transition duration-300 shadow-md ${loading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            whileTap={{ scale: 0.95 }}
          >
            {loading ? "Signing In..." : "Sign In"}
          </motion.button>
        </form>

        {errorMessage && <p className="text-red-400 text-sm text-center mt-4">{errorMessage}</p>}
        {/* <p className="text-sm text-white/80 text-center mt-2">
          <Link to="/forgot-password" className="text-yellow-300 hover:underline">
            Forgot Password?
          </Link>
        </p> */}

        <p className="text-sm text-white/80 text-center mt-6">
          Don’t have an account?{" "}
          <Link to="/signup" className="text-yellow-300 hover:underline">Sign Up</Link>
        </p>
      </motion.div>
    </motion.div>
  );
}

function InputWithIcon({ id, icon, type = "text", placeholder, value, onChange }) {
  return (
    <div className="relative">
      <label htmlFor={id} className="block text-white mb-1 capitalize">
        {placeholder}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        required
        className="w-full bg-white/20 text-white placeholder-white/70 px-11 py-3 rounded-lg border border-white/30 focus:ring-2 focus:ring-yellow-300 focus:outline-none"
        placeholder={placeholder}
      />
      <FontAwesomeIcon icon={icon} className="absolute top-1/2 left-4 transform -translate-y-1/2 text-white/80" />
    </div>
  );
}
